import * as React from "react";
import { AlertColor } from "@mui/material";
import {
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GridRenderCellParams,
    DataGridPro,
    GridColumns,
    GridSelectionModel,
} from "@mui/x-data-grid-pro";
import CclDataGridToolbar from "../../../../../components/common/cclDataGridToolbar/cclDataGridToolbar";
import { addCustomDataGridFilters } from "../../../../../components/common/customDataGridFilters/customDataGridFilters";
import {
    useGetArchivedProductSaleParticipantsByFacilitatorEmailQuery,
    useUnarchiveProductSaleParticipantsMutation,
} from "../../../../../services/cclTokenedEnterpriseParticipantApi";
import CclAlertSnackbar from "../../../../../components/common/cclAlertSnackbar";
import useLogAccessEvent from "../../../../../hooks/useLogAccessEvent";

interface ArchivedProductSalesDataGridProps {
    facilitatorEmail: string;
}

const ArchivedProductSalesDataGrid: React.FC<ArchivedProductSalesDataGridProps> = (props) => {
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor | undefined>("info");
    const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
    const [unarchiving, setUnarchiving] = React.useState<boolean>(false);
    const {
        data: archivedProductSales,
        isLoading,
        isError,
    } = useGetArchivedProductSaleParticipantsByFacilitatorEmailQuery(props.facilitatorEmail);
    const [unarchiveProductSales] = useUnarchiveProductSaleParticipantsMutation(); // unarchive product sale
    const { logEvent } = useLogAccessEvent();

    const ShowSnackbarElement = (message: string, severity: AlertColor) => {
        setShowSnackbar(true);
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
    };

    React.useEffect(() => {
        if (isError === true) {
            ShowSnackbarElement("Error Retrieving Archived Product Sales", "error");
        }
    }, [isError]);

    const handleUnarchive = () => {
        if (selectedIds.length > 0) {
            setUnarchiving(true);
            unarchiveProductSales({ reportIds: selectedIds })
                .unwrap()
                .then((result) => {
                    const failures = result.filter((r) => r.isArchived);
                    if (failures.length === 0) {
                        logEvent("ParticipantsUnarchived", null);
                        ShowSnackbarElement("Participant(s) Un-Archived", "success");
                    } else if (failures.length < result.length) {
                        ShowSnackbarElement("Some Participant(s) Un-Archive Failed", "warning");
                    } else {
                        ShowSnackbarElement("Participant(s) Un-Archive Failed", "error");
                    }
                })
                .catch(() => {
                    ShowSnackbarElement("Participant(s) Un-Archive Failed", "error");
                })
                .finally(() => {
                    setUnarchiving(false);
                });
        }
    };

    const COLUMNS = [
        {
            ...GRID_CHECKBOX_SELECTION_COL_DEF,
        },
        {
            field: "fullName",
            headerName: "Participant",
            type: "string",
            flex: 1,
        },
        {
            field: "assessmentType",
            headerName: "Assessment",
            type: "string",
            flex: 0.75,
        },
        {
            field: "scoreDate",
            headerName: "Scored",
            valueGetter: (params: any) => new Date(params.row.scoreDate),
            type: "string",
            renderCell: (params: GridRenderCellParams) => (
                <span>{params.value.toLocaleDateString()}</span>
            ),
            width: 150,
        },
    ];

    return (
        <React.Fragment>
            {showSnackbar ? (
                <CclAlertSnackbar
                    open={true}
                    onClose={() => setShowSnackbar(false)}
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                />
            ) : null}
            <DataGridPro
                density="compact"
                getRowId={(row) => row.reportId}
                rows={(archivedProductSales ?? []).filter((r) => r.reportId != null)}
                columns={addCustomDataGridFilters(COLUMNS as GridColumns<any>)}
                components={{
                    Toolbar: CclDataGridToolbar,
                }}
                componentsProps={{
                    toolbar: {
                        showSaveState: true,
                        hideSettingsButtons: true,
                        miscButtons: [
                            {
                                id: "unarchive",
                                caption: "Un-Archive Participant(s)",
                                label: "Un-Archive Participant(s)",
                                disable: selectedIds?.length <= 0,
                                onClick: () => handleUnarchive(),
                                loadingBtn: true,
                                isLoading: unarchiving,
                            },
                        ],
                        btnClick: () => handleUnarchive(),
                    },
                }}
                loading={isLoading}
                disableSelectionOnClick
                checkboxSelection={true}
                onSelectionModelChange={(reportIds: GridSelectionModel) => {
                    setSelectedIds([...reportIds.map((rid) => rid as string)]);
                }}
                initialState={{
                    sorting: {
                        sortModel: [{ field: "fullName", sort: "asc" }],
                    },
                }}
                sx={{
                    "& .MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                        outline: "none !important",
                    },
                }}
                autoHeight
            />
        </React.Fragment>
    );
};
export default ArchivedProductSalesDataGrid;
